import { useState } from "react";
import { X } from "lucide-react";
import { Eyebrow } from "./Eyebrow";
import { useModalDismiss } from "../hooks/use-modal";

const INPUT_CLASS =
  "w-full border-b border-[#DCD7C9]/25 bg-transparent py-3 text-[#F4F1EA] placeholder:text-[#DCD7C9]/40 outline-none transition-colors duration-300 focus:border-[#A27B5B]";

function SuccessNote({ onClose }) {
  return (
    <div data-testid="contact-success" className="mt-10">
      <p className="text-[1.4rem] font-medium text-[#F4F1EA]">Thank you.</p>
      <p className="mt-3 text-[#DCD7C9]/70">
        We’ve received your note and will be in touch.
      </p>
      <button
        type="button"
        onClick={onClose}
        className="mt-8 text-xs uppercase tracking-[0.2em] text-[#A27B5B]"
      >
        Close
      </button>
    </div>
  );
}

function ContactForm({ form, status, onChange, onSubmit }) {
  return (
    <form onSubmit={onSubmit} data-testid="contact-form" className="mt-8 space-y-6">
      <p className="text-[#DCD7C9]/80">
        Building something? We’d like to hear about it.
      </p>
      <input
        required
        type="text"
        placeholder="Your name"
        value={form.name}
        onChange={onChange("name")}
        data-testid="contact-name"
        className={INPUT_CLASS}
      />
      <input
        required
        type="email"
        placeholder="Email"
        value={form.email}
        onChange={onChange("email")}
        data-testid="contact-email-input"
        className={INPUT_CLASS}
      />
      <textarea
        required
        rows={4}
        placeholder="Tell us a little about it"
        value={form.message}
        onChange={onChange("message")}
        data-testid="contact-message"
        className={`${INPUT_CLASS} resize-none`}
      />
      {status === "error" && (
        <p data-testid="contact-error" className="text-sm text-[#A27B5B]">
          Something went wrong. Please try again.
        </p>
      )}
      <button
        type="submit"
        disabled={status === "submitting"}
        data-testid="contact-submit"
        className="inline-flex items-center gap-2 border-b border-[#DCD7C9] pb-1 text-[1.05rem] tracking-tight text-[#DCD7C9] transition-colors duration-300 hover:border-[#A27B5B] hover:text-[#A27B5B] disabled:opacity-50"
      >
        {status === "submitting" ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}

export function ContactModal({ onClose }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  useModalDismiss(onClose);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div
      data-testid="contact-modal"
      onClick={onClose}
      className="modal-overlay fixed inset-0 z-[100] flex items-center justify-center bg-[#141414]/85 p-5 backdrop-blur-sm sm:p-8"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="modal-panel relative max-h-[90vh] w-full max-w-[560px] overflow-y-auto bg-[#2C3639] p-7 sm:p-10"
      >
        <div className="flex items-center justify-between">
          <Eyebrow dark>Get in touch</Eyebrow>
          <button
            type="button"
            onClick={onClose}
            data-testid="contact-modal-close"
            aria-label="Close"
            className="text-[#DCD7C9] transition-colors duration-300 hover:text-[#A27B5B]"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {status === "success" ? (
          <SuccessNote onClose={onClose} />
        ) : (
          <ContactForm form={form} status={status} onChange={update} onSubmit={submit} />
        )}
      </div>
    </div>
  );
}
